import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Button,
  Card,
  Form,
  Table,
} from "react-bootstrap";
import {
  FaColumns,
  FaTable,
  FaSave,
  FaStickyNote,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaMinusSquare,
  FaTrashAlt,
} from "react-icons/fa";
import axios from "axios";
import "./QuickNotes.css";

export default function QuickNotes({ userData }) {
  const [headers, setHeaders] = useState(["Column 1", "Column 2"]);
  const [rows, setRows] = useState([["", ""], ["", ""]]);
  const [bottomNote, setBottomNote] = useState("");
  const [lastSaved, setLastSaved] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [side, setSide] = useState("left");

  // Fetch previous notes
  useEffect(() => {
    console.log("Fetching notes for:", userData);
    if (!userData?.branch || !userData?.token || !userData?.userId) return;
      if (userData?.branch && userData?.token && userData?.userId)
    axios
      .get(`${process.env.REACT_APP_COLLEGE_ERP_API}xnotes`, {
        params: {
          branch: userData.branch,
          userId: userData.userId,
          userName: userData.name,
        },
        headers: { Authorization: `Bearer ${userData.token}` },
      })
      .then((res) => {
      
        console.log("Fetched notes:", res.data);
        if (res.data?.length > 0) {
          const latest = res.data[res.data.length - 1];
          setHeaders(latest.headers || ["Column 1", "Column 2"]);
          setRows(latest.rows || [["", ""]]);
          setBottomNote(latest.bottomNote || "");
        }
      })
      .catch((err) => console.error("Fetch notes error:", err));
  }, [userData?.branch, userData?.token]);

  // Save notes
  const saveNotes = async () => {
    if (!userData?.branch || !userData?.userId) return;
    try {
      const notesData = { headers, rows, bottomNote, createdBy: userData.userId };
      await axios.put(`${process.env.REACT_APP_COLLEGE_ERP_API}xnotes`, notesData, {
        params: { branch: userData.branch, userId: userData.userId, userName: userData.name },
        headers: { Authorization: `Bearer ${userData.token}` },
      });
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error("Failed to save note:", err);
    }
  };

  const saveBottomNote = () => saveNotes();

  const handleHeaderChange = (i, v) => {
    const newHeaders = [...headers];
    newHeaders[i] = v;
    setHeaders(newHeaders);
  };

  const handleCellChange = (r, c, v) => {
    const newRows = [...rows];
    newRows[r][c] = v;
    setRows(newRows);
  };

  const addColumn = () => {
    setHeaders([...headers, `Column ${headers.length + 1}`]);
    setRows(rows.map((r) => [...r, ""]));
    saveNotes();
  };

  const addRow = () => {
    setRows([...rows, Array(headers.length).fill("")]);
    saveNotes();
  };

  const removeColumn = (colIndex) => {
    if (headers.length <= 1) return alert("At least one column is required.");
    setHeaders(headers.filter((_, i) => i !== colIndex));
    setRows(rows.map((r) => r.filter((_, i) => i !== colIndex)));
    saveNotes();
  };

  const removeRow = (rowIndex) => {
    if (rows.length <= 1) return alert("At least one row is required.");
    setRows(rows.filter((_, i) => i !== rowIndex));
    saveNotes();
  };

  return (
    <>
      {/* Draggable Floating Icon */}
      <motion.div
        drag
        dragMomentum={false}
        style={{
          position: "fixed",
          bottom: 30,
          [side]: 30,
          width: 50,
          height: 50,
          backgroundColor: "#0d6efd",
          borderRadius: "50%",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "grab",
          zIndex: 1100,
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FaStickyNote size={22} />
      </motion.div>

      {/* Draggable Panel */}
      {isOpen && (
        <motion.div
          drag
          dragMomentum={false}
          dragElastic={0.2}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          style={{
            width: 450,
            height: 500,
            top: 100,
            [side]: 30,
            position: "fixed",
            zIndex: 1050,
            cursor: "grab",
          }}
        >
          <Card className="shadow">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setSide(side === "right" ? "left" : "right")}
                >
                  {side === "right" ? <FaChevronLeft /> : <FaChevronRight />}
                </Button>
                <strong>Quick Notes</strong>
              </div>
              <div>
                {lastSaved && <small className="text-muted me-2">Last saved: {lastSaved}</small>}
                <Button size="sm" variant="danger" onClick={() => setIsOpen(false)}>
                  <FaTimes />
                </Button>
              </div>
            </Card.Header>
            <Card.Body style={{ overflowY: "auto" }}>
              <div className="mb-2 d-flex gap-2 flex-wrap">
                <Button variant="info" size="sm" onClick={addRow}>
                  <FaTable className="me-1" /> Add Row
                </Button>
                <Button variant="secondary" size="sm" onClick={addColumn}>
                  <FaColumns className="me-1" /> Add Column
                </Button>
                <Button variant="success" size="sm" onClick={saveNotes}>
                  <FaSave className="me-1" /> Save
                </Button>
              </div>
           <div style={{ maxHeight: "350px", overflowY: "auto" }}>
  <Table bordered hover size="sm">
    <thead className="table-primary">
      <tr>
        {headers.map((header, i) => (
          <th key={i}>
            <div className="d-flex gap-2 align-items-center">
              <Form.Control
                size="sm"
                value={header}
                onChange={(e) => handleHeaderChange(i, e.target.value)}
                onBlur={saveNotes}
              />
              <FaMinusSquare
                className="text-danger cursor-pointer"
                onClick={() => removeColumn(i)}
              />
            </div>
          </th>
        ))}
      </tr>
    </thead>

    <tbody>
      {rows.map((row, r) => (
        <tr key={r}>
          {row.map((cell, c) => (
            <td key={c}>
              <Form.Control
                size="sm"
                value={cell}
                onChange={(e) => handleCellChange(r, c, e.target.value)}
                onBlur={saveNotes}
              />
            </td>
          ))}
          <td style={{ width: 40, textAlign: "center" }}>
            <FaTrashAlt
              className="text-danger cursor-pointer"
              onClick={() => removeRow(r)}
            />
          </td>
        </tr>
      ))}

      <tr>
        <td colSpan={headers.length + 1}>
        <Form.Control
  as="textarea"
  placeholder="Additional notes..."
  value={bottomNote}
  onChange={(e) => setBottomNote(e.target.value)}
  onBlur={saveBottomNote}
  rows={3}
  style={{ minHeight: "100px" }}   // 👈 adjust as needed
/>

        </td>
      </tr>
    </tbody>
  </Table>
</div>

            </Card.Body>
          </Card>
        </motion.div>
      )}
    </>
  );
}
